import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import { Download, Trash2 } from "lucide-react";
import { useAppDispatch } from "../../app/hooks";
import {
  eraseMember,
  exportMemberData,
} from "../../features/members/slice-extensions/members-slice-extension";
import { fetchErasurePreview } from "../../features/members/slice-extensions/member-erasure-slice-extension";
import { Member } from "../../features/members/models/Member";
import { KeptTrainingRecord } from "../../features/members/models/MemberErasure";
import {
  erasureToastText,
  joinSentences,
  trainingHoldSentences,
} from "../../features/members/utils/trainingHoldText";
import { ROUTES } from "../../routes";
import ConfirmationModal from "../ConfirmationModal";

interface MemberDataRightsSectionProps {
  member: Member;
}

/**
 * Data-subject rights for a member: export everything held about them (GDPR Art. 15/20) as a
 * downloadable JSON file, and erase their personal data (Art. 17). Erasure is irreversible and
 * gated behind a confirmation.
 */
const MemberDataRightsSection: React.FC<MemberDataRightsSectionProps> = ({ member }) => {
  const { t, i18n } = useTranslation();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const [isExporting, setIsExporting] = useState(false);
  const [isErasing, setIsErasing] = useState(false);
  const [showEraseConfirm, setShowEraseConfirm] = useState(false);
  const [keptRecords, setKeptRecords] = useState<KeptTrainingRecord[] | null>(null);
  const memberName = `${member.firstName} ${member.lastName}`.trim();

  // The confirmation names any training record the erasure keeps under legal hold (PD-0021), so
  // confirm stays disabled until the preview has arrived; without it the dialog closes again.
  const openEraseConfirm = async () => {
    setKeptRecords(null);
    setShowEraseConfirm(true);
    const preview = await dispatch(fetchErasurePreview(member.id));
    if (fetchErasurePreview.fulfilled.match(preview)) {
      setKeptRecords(preview.payload);
    } else {
      toast.error(t("members.dataRights.previewFailed", "Couldn't check what the erasure keeps. Nothing was erased. Try again."));
      setShowEraseConfirm(false);
    }
  };

  const handleExport = async () => {
    setIsExporting(true);
    try {
      const result = await dispatch(exportMemberData(member.id));
      if (exportMemberData.fulfilled.match(result)) {
        const blob = new Blob([JSON.stringify(result.payload, null, 2)], {
          type: "application/json",
        });
        const url = URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.download = `member-${member.id}-data-export.json`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
        toast.success(t("members.dataRights.exported", "Member data exported."));
      } else {
        toast.error(result.payload?.message || t("members.dataRights.exportFailed", "Failed to export member data."));
      }
    } finally {
      setIsExporting(false);
    }
  };

  const handleConfirmErase = async () => {
    setIsErasing(true);
    try {
      const result = await dispatch(eraseMember(member.id));
      if (eraseMember.fulfilled.match(result)) {
        toast.success(erasureToastText(t, result.payload, i18n.language, "members.dataRights.erased"));
        navigate(ROUTES.MEMBERS);
      } else {
        toast.error(result.payload?.message || t("members.dataRights.eraseFailed", "Failed to erase member data."));
        setShowEraseConfirm(false);
      }
    } finally {
      setIsErasing(false);
    }
  };

  return (
    <div className="with-border rounded-2xl p-4 lg:p-6">
      <h3 className="text-grey-10 dark:text-grey-90 mb-2">
        {t("members.dataRights.title", "Data & privacy rights")}
      </h3>
      <p className="text-grey-30 dark:text-grey-70 text-sm mb-4">
        {t("members.dataRights.subtitle", "Export everything held about this member, or permanently erase their personal data on request.")}
      </p>

      <div className="flex flex-col sm:flex-row sm:items-center gap-3">
        <button
          type="button"
          onClick={handleExport}
          disabled={isExporting}
          className="btn btn-secondary inline-flex items-center gap-2 disabled:opacity-50"
        >
          <Download className="w-4 h-4" />
          {isExporting
            ? t("members.dataRights.exporting", "Exporting...")
            : t("members.dataRights.export", "Export data")}
        </button>

        <button
          type="button"
          onClick={openEraseConfirm}
          disabled={isErasing}
          className="inline-flex items-center gap-2 rounded-lg bg-red-600 px-4 py-2 text-sm font-medium text-white hover:bg-red-700 disabled:opacity-50 dark:bg-red-700 dark:hover:bg-red-600"
        >
          <Trash2 className="w-4 h-4" />
          {t("members.dataRights.erase", "Erase data")}
        </button>
      </div>

      <ConfirmationModal
        isOpen={showEraseConfirm}
        title={t("members.dataRights.eraseTitle", "Erase member data")}
        message={joinSentences([
          t("members.dataRights.eraseNamed", "Erase the personal data of {{name}}? This can't be undone.", { name: memberName }),
          t("members.dataRights.eraseMessage", "Their name, email, notes and date of birth are removed. Their billing history stays, without identifying details. They are also taken off all their {{sessions}} and waitlists."),
          t("members.dataRights.eraseMessages", "Their messages are anonymised and the files they sent are deleted."),
          trainingHoldSentences(t, keptRecords ?? [], i18n.language, "members.dataRights.trainingHeld"),
        ], i18n.language)}
        confirmText={
          isErasing
            ? t("members.dataRights.erasing", "Erasing...")
            : t("members.dataRights.erase", "Erase data")
        }
        cancelText={t("members.dataRights.keepMember", "Keep {{member}}")}
        variant="danger"
        confirmVariant="danger"
        confirmPending={keptRecords === null || isErasing}
        onConfirm={handleConfirmErase}
        onCancel={() => setShowEraseConfirm(false)}
      />
    </div>
  );
};

export default MemberDataRightsSection;
